/* eslint-disable prettier/prettier */
import { FunctionComponent, useEffect, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { handlingUrlParams } from "../../Utils/AppUtils";
import AdminServices from "services/admin-services";
import { Button } from "primereact/button";
import { Sidebar } from "primereact/sidebar";
import PackageFilters from "./filters";
import Loader from "common/Loader";
import PackageDocTypes from "./viewDocTypes";
import AddPackage from "components/investorAdmin/Addpackage";
// import UploadPackage from "./UploadPackage";
import { Dialog } from "primereact/dialog";
import { SplitButton } from "primereact/splitbutton";
import { MenuItem } from "primereact/menuitem";
import { Tag } from "primereact/tag";
import moment from "moment";
import * as XLSX from "xlsx";
import { setToaster } from "common/toastStore";

const InvestorPackage: FunctionComponent = () => {
  const MAX_EXCEL_CELL_LENGTH = 32767;

  const clampExcelCellText = (value: any) => {
    if (value === null || value === undefined) return "";

    const text = typeof value === "string" ? value : String(value);
    if (text.length <= MAX_EXCEL_CELL_LENGTH) return text;

    return `${text.slice(0, MAX_EXCEL_CELL_LENGTH - 3)}...`;
  };

  const [customerData, setCustomerData] = useState<any[]>([]);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [filtersVisible, setFiltersVisible] = useState(false);
  const [docTypesVisible, setDocTypesVisible] = useState(false);
  const [loader, setLoader] = useState<boolean>(false);
  const [editingRowId, setEditingRowId] = useState<number | null>(null);
  const [rowLoadingId, setRowLoadingId] = useState<number | null>(null);
  const [originalRowCache, setOriginalRowCache] = useState<Record<number, any>>(
    {},
  );
  const [addPackageVisible, setAddPackageVisible] = useState(false);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<any>(null);
  const [packageData, setPackageData] = useState<any>(null);

  const [appliedFilters, setAppliedFilters] = useState<any>({
    name: "",
    documentReference: "",
    createdFrom: null,
    createdTo: null,
    includeInactive: false,
  });

  const [lazyParams, setLazyParams] = useState({
    first: 0,
    rows: 10,
    page: 1,
  });

  useEffect(() => {
    loadLazyData();
  }, [lazyParams, appliedFilters]);

  const buildParams = (overrides: Record<string, any> = {}) => {
    const params: Record<string, any> = {
      PageNumber: lazyParams.page,
      PageSize: lazyParams.rows,
      IncludeDocumentType: false,
      IncludeInactive: appliedFilters.includeInactive ?? false,
      ...overrides,
    };

    if (appliedFilters.name?.trim()) {
      params.SearchTerm = appliedFilters.name.trim();
    }

    if (appliedFilters.documentReference?.trim()) {
      params.DocumentType = appliedFilters.documentReference.trim();
    }

    if (appliedFilters.createdFrom) {
      params.StartDate = appliedFilters.createdFrom.toISOString();
    }

    if (appliedFilters.createdTo) {
      params.EndDate = appliedFilters.createdTo.toISOString();
    }

    return params;
  };

  const loadLazyData = () => {
    setLoading(true);

    const params = buildParams();

    AdminServices.getInvestorPackages(handlingUrlParams(params))
      .then((response: any) => {
        const parsedHeaders = JSON.parse(response.headers["x-pagination"]);
        setCustomerData(response.data);
        setTotalRecords(parsedHeaders.TotalCount);
      })
      .finally(() => setLoading(false));
  };

  const exportData = async () => {
    setLoading(true);
    try {
      const params = buildParams({
        PageNumber: 1,
        PageSize: 5000,
        IncludeDocumentType: true,
      });

      const response: any = await AdminServices.exportInvestorPackages(
        handlingUrlParams(params),
      );

      const data = Array.isArray(response?.data?.data)
        ? response.data.data
        : Array.isArray(response?.data)
        ? response.data
        : [];

      if (!data || data.length === 0) {
        setToaster({
          severity: "info",
          summary: "No data",
          detail: "No records to export.",
        });
        return;
      }

      const exportRows = data.flatMap((item: any) => {
        const docTypes = Array.isArray(item?.documentTypes)
          ? [...item.documentTypes].sort(
              (a: any, b: any) => (a?.processOrder || 0) - (b?.processOrder || 0),
            )
          : [];

        if (!docTypes.length) {
          return [
            {
              Id: item?.id,
              Name: clampExcelCellText(item?.name),
              Description: clampExcelCellText(item?.description),
              "Place Holder": "",
              "Place Holder Process Order": "",
              "Is Active": item?.isActive,
            },
          ];
        }

        return docTypes.map((docType: any) => ({
          Id: item?.id,
          Name: clampExcelCellText(item?.name),
          Description: clampExcelCellText(item?.description),
          "Place Holder": clampExcelCellText(docType?.name),
          "Place Holder Process Order": docType?.processOrder ?? "",
          "Is Active": item?.isActive,
        }));
      });

      const ws = XLSX.utils.json_to_sheet(exportRows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Packages");

      const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([wbout], { type: "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `packages_export_${new Date().toISOString()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e: any) {
      console.error(e);
      setToaster({
        severity: "error",
        summary: "Export failed",
        detail: e?.message || "Error",
      });
    } finally {
      setLoading(false);
    }
  };

  const onPage = (event: any) => {
    setLazyParams({
      ...lazyParams,
      first: event.first,
      rows: event.rows,
      page: event.page + 1,
    });
  };

  const handleApplyFilters = (filters: any) => {
    setLazyParams((prev) => ({ ...prev, page: 1, first: 0 }));
    setAppliedFilters(filters);
    setFiltersVisible(false);
  };

  const handleEdit = (rowData: any) => {
    setOriginalRowCache((prev) => ({
      ...prev,
      [rowData.id]: { ...rowData },
    }));
    setEditingRowId(rowData.id);
  };

  const handleCancel = (rowData: any) => {
    const original = originalRowCache[rowData.id];
    if (original) {
      const restored = customerData.map((item) =>
        item.id === rowData.id ? original : item,
      );
      setCustomerData(restored);
    }
    setEditingRowId(null);
  };

  const handleSave = async (rowData: any) => {
    if (!rowData.name?.trim()) return;

    try {
      setRowLoadingId(rowData.id);
      setLoader(true);
      await AdminServices.updateInvestorPackages(rowData);

      const updated = customerData.map((item) =>
        item.id === rowData.id
          ? {
              ...item,
              name: rowData.name.trim(),
              description: rowData.description,
            }
          : item,
      );

      setCustomerData(updated);
      setEditingRowId(null);
      setLoader(false);
    } catch (error) {
      handleCancel(rowData);
    } finally {
      setRowLoadingId(null);
    }
  };

  const handleRowDelete = async () => {
    if (!rowToDelete) return;

    try {
      setLoader(true);

      const payLoadModified = {
        ...rowToDelete,
        isActive: false,
      };

      await AdminServices.updateInvestorPackages(payLoadModified);

      setCustomerData((prev) =>
        prev.filter((item) => item.id !== rowToDelete.id),
      );

      setDeleteDialogVisible(false);
      setRowToDelete(null);
      loadLazyData();
    } catch (error) {
      console.warn("Delete failed", error);
    } finally {
      setLoader(false);
    }
  };

  const nameBodyTemplate = (rowData: any) => {
    const isEditing = editingRowId === rowData.id;

    if (!isEditing) return rowData.name;

    return (
      <input
        type="text"
        value={rowData.name}
        onChange={(e) => {
          const updated = customerData.map((item) =>
            item.id === rowData.id ? { ...item, name: e.target.value } : item,
          );
          setCustomerData(updated);
        }}
        className="p-inputtext p-component w-full"
      />
    );
  };

  const descriptionBodyTemplate = (rowData: any) => {
    const isEditing = editingRowId === rowData.id;

    if (!isEditing) return rowData.description;

    return (
      <input
        type="text"
        value={rowData.description || ""}
        onChange={(e) => {
          const updated = customerData.map((item) =>
            item.id === rowData.id
              ? { ...item, description: e.target.value }
              : item,
          );
          setCustomerData(updated);
        }}
        className="p-inputtext p-component w-full"
      />
    );
  };

  const createdDateBodyTemplate = (rowData: any) => {
    const date = rowData?.createdDate;
    if (!date || !moment(date).isValid()) return <span>NA</span>;
    return <span>{moment(date).format("MM/DD/YYYY hh:mm A")}</span>;
  };

  const updatedDateBodyTemplate = (rowData: any) => {
    const date = rowData?.updatedDate;
    if (!date || !moment(date).isValid()) return <span></span>;
    return <span>{moment(date).format("MM/DD/YYYY hh:mm A")}</span>;
  };

  const activeTemplate = (rowData: any) => {
    const isActive = rowData?.isActive === true;

    return (
      <Tag
        value={isActive ? "Yes" : "No"}
        severity={isActive ? "success" : "danger"}
      />
    );
  };

  const actionBodyTemplate = (rowData: any) => {
    const isEditing = editingRowId === rowData.id;
    const isLoading = rowLoadingId === rowData.id;

    if (isLoading) {
      return <i className="pi pi-spin pi-spinner" />;
    }

    const actionItems: MenuItem[] = [
      {
        label: "Edit",
        icon: "pi pi-pencil",
        command: () => handleEdit(rowData),
      },
      {
        label: "View",
        icon: "pi pi-eye",
        command: () => getPackageDocTypesByID(rowData),
      },
      // {
      //   label: "Add Placholder",
      //   icon: "pi pi-plus",
      //   command: () => {},
      // },
      {
        label: "Delete",
        icon: "pi pi-trash",
        command: () => {
          setRowToDelete(rowData);
          setDeleteDialogVisible(true);
        },
      },
    ];

    return (
      <div className="flex gap-2 justify-center">
        {!isEditing ? (
          <SplitButton
            label="Actions"
            icon="pi pi-cog"
            model={actionItems}
            className="p-button-sm p-button-outlined p-button-secondary"
            onClick={() => getPackageDocTypesByID(rowData)}
          />
        ) : (
          <>
            <Button
              icon="pi pi-check"
              className="p-button-sm p-button-outlined p-button-success"
              onClick={() => handleSave(rowData)}
            />
            <Button
              icon="pi pi-times"
              className="p-button-sm p-button-outlined p-button-danger"
              onClick={() => handleCancel(rowData)}
            />
          </>
        )}
      </div>
    );
  };

  const getPackageDocTypesByID = (rowData: any) => {
    setLoader(true);
    AdminServices.getPackageDocTypesByID(
      handlingUrlParams({
        PackageId: rowData?.id,
      }),
    )
      .then((response: any) => {
        setPackageData(rowData);
        setSelectedRow(response?.data);
        setDocTypesVisible(true);
        setLoader(false);
      })
      .catch((e: Error) => {
        console.warn(e);
        setLoader(false);
      });
  };

  return (
    <>
      <DataTable
        value={customerData}
        className="p-datatable-sm"
        loading={loading}
        lazy
        paginator
        totalRecords={totalRecords}
        rows={lazyParams.rows}
        first={lazyParams.first}
        onPage={onPage}
        dataKey="id"
        rowsPerPageOptions={[10, 20, 50]}
        paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
        header={
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold">List of Packages</h2>
            <div className="flex flex-row gap-4">
              <Button
                onClick={() => setAddPackageVisible(true)}
                label="Add Package"
                icon="pi pi-plus"
                className="p-button-sm p-button-outlined p-button-success"
              />

              <Button
                onClick={() => setFiltersVisible(true)}
                icon="pi pi-filter"
                label="Filters"
                className="p-button-sm p-button-outlined p-button-warning"
              />

              <Button
                icon="pi pi-file-export"
                label="Export"
                className="p-button-sm p-button-outlined p-button-primary"
                onClick={() => exportData()}
              />
            </div>
          </div>
        }
        responsiveLayout="scroll"
        emptyMessage="No Packages found."
      >
        <Column
          field="name"
          header="Name"
          body={nameBodyTemplate}
          style={{ minWidth: "250px" }}
        />

        <Column
          field="description"
          header="Description"
          body={descriptionBodyTemplate}
          style={{ minWidth: "300px" }}
        />

        <Column
          field="createdDate"
          header="Created Date"
          body={createdDateBodyTemplate}
          style={{ minWidth: "180px" }}
        />

        <Column
          field="createdBy"
          header="Created By"
          style={{ minWidth: "180px" }}
        />

        <Column
          field="updatedDate"
          header="Updated Date"
          body={updatedDateBodyTemplate}
          style={{ minWidth: "180px" }}
        />

        <Column
          field="updatedBy"
          header="Updated By"
          style={{ minWidth: "180px" }}
        />

        <Column
          field="isActive"
          header="Active"
          body={activeTemplate}
          style={{ minWidth: "120px" }}
        />

        <Column
          header="Actions"
          body={actionBodyTemplate}
          headerStyle={{ width: "180px" }}
          bodyStyle={{ textAlign: "center" }}
        />
      </DataTable>

      <Sidebar
        visible={filtersVisible}
        position="right"
        onHide={() => setFiltersVisible(false)}
        style={{ width: 520 }}
      >
        <PackageFilters
          onHideFilters={() => setFiltersVisible(false)}
          onApplyFilters={handleApplyFilters}
          initialValues={appliedFilters}
        />
      </Sidebar>
      <Sidebar
        visible={addPackageVisible}
        position="right"
        onHide={() => setAddPackageVisible(false)}
        style={{ width: 520 }}
      >
        <AddPackage
          onClose={() => setAddPackageVisible(false)}
          customerId={customerData?.[0]?.customerId}
          onSubmitSuccess={loadLazyData}
        />
      </Sidebar>
      <Sidebar
        visible={docTypesVisible}
        position="right"
        onHide={() => setDocTypesVisible(false)}
        style={{ width: "70vw" }}
      >
        <PackageDocTypes selectedRow={selectedRow} packageData={packageData} />
      </Sidebar>

      {loader ? <Loader /> : null}
      <Dialog
        header="Confirm Delete"
        visible={deleteDialogVisible}
        style={{ width: "400px" }}
        modal
        onHide={() => setDeleteDialogVisible(false)}
        footer={
          <div className="flex justify-content-end flex-row-reverse gap-2">
            <Button
              onClick={handleRowDelete}
              label="Yes"
              className="p-button-sm p-button-outlined p-button-success"
            />
            <Button
              onClick={() => setDeleteDialogVisible(false)}
              label="Cancel"
              className="p-button-sm p-button-outlined p-button-warning"
            />
          </div>
        }
      >
        <p>
          Are you sure you want to delete <strong>{rowToDelete?.name}</strong> ?
        </p>
      </Dialog>
    </>
  );
};

export default InvestorPackage;
